import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, Eye, EyeOff, Loader2, Pin, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Seo } from "@/components/Seo";

const btn =
  "inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm transition-colors hover:bg-card disabled:opacity-50";

export default function AdminTrending({ embedded = false }) {
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState([]);
  const [names, setNames] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const loadPreview = useCallback(async () => {
    const r = await api.get("/cast-changes/recently-updated", { params: { limit: 20 } });
    setPreview(r.data || []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [c, a] = await Promise.all([api.get("/admin/trending"), api.get("/actors")]);
        setCfg(c.data);
        setNames((a.data || []).map((x) => x.name).sort((x, y) => x.localeCompare(y)));
        await loadPreview();
      } catch (e) {
        toast.error(apiError(e.response?.data?.detail) || e.message);
      }
    })();
  }, [loadPreview]);

  const save = async (patch, okMsg) => {
    setBusy(true);
    try {
      const r = await api.put("/admin/trending", patch);
      setCfg(r.data);
      await loadPreview();
      if (okMsg) toast.success(okMsg);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const lc = (s) => (s || "").trim().toLowerCase();
  const shownNames = useMemo(() => new Set(preview.map((e) => lc(e.actor_name))), [preview]);

  if (!cfg) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#C89B3C]" />
      </div>
    );
  }

  const hide = (name) =>
    save(
      {
        hidden: [...cfg.hidden.filter((n) => lc(n) !== lc(name)), name],
        pinned: cfg.pinned.filter((n) => lc(n) !== lc(name)),
      },
      `${name} hidden from Trending`
    );
  const unhide = (name) =>
    save({ hidden: cfg.hidden.filter((n) => lc(n) !== lc(name)) }, `${name} can appear again`);
  const unpin = (name) => save({ pinned: cfg.pinned.filter((n) => lc(n) !== lc(name)) }, `${name} unpinned`);
  const move = (i, d) => {
    const next = [...cfg.pinned];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    save({ pinned: next });
  };
  const pin = (e) => {
    e.preventDefault();
    const typed = input.trim();
    if (!typed) return;
    const name = names.find((n) => lc(n) === lc(typed)) || typed;
    if (cfg.pinned.some((n) => lc(n) === lc(name))) {
      toast.info(`${name} is already pinned`);
      return;
    }
    save(
      {
        pinned: [...cfg.pinned, name],
        hidden: cfg.hidden.filter((n) => lc(n) !== lc(name)),
      },
      `${name} pinned`
    ).then(() => setInput(""));
  };

  return (
    <div className={embedded ? "" : "mx-auto max-w-3xl px-4 py-10 sm:px-6"}>
    {!embedded && (
      <>
        <Seo title="Manage Trending" noindex />
        <Link to="/admin" className="text-sm text-muted-foreground hover:underline">
          &larr; Admin
        </Link>
        <h1 className="mt-3 font-serif text-3xl font-semibold">Trending now</h1>
      </>
    )}
    <p className={embedded ? "text-muted-foreground" : "mt-2 text-muted-foreground"}>
        Choose who appears in the Trending section of the Casts page. Pinned people come first, in the order
        below. People in closed shows, or who have left a role, never appear.
      </p>

      <section className="mt-8 rounded-2xl border border-border bg-card p-5" data-testid="trending-mode">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={cfg.manual_only}
            disabled={busy}
            onChange={(e) =>
              save(
                { manual_only: e.target.checked },
                e.target.checked ? "Trending now shows only your pinned people" : "Automatic list switched back on"
              )
            }
          />
          <span>
            <span className="font-medium">Manual only</span>
            <span className="block text-sm text-muted-foreground">
              Show only the people pinned below. Turn this off to fill the remaining places automatically
              with the most recent cast changes.
            </span>
          </span>
        </label>
      </section>

      <section className="mt-8" data-testid="trending-pinned">
        <h2 className="font-serif text-xl font-semibold">Pinned</h2>
        <form onSubmit={pin} className="mt-3 flex flex-wrap gap-2">
          <input
            list="trending-names"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Start typing a performer's name"
            className="min-w-0 flex-1 rounded-xl border border-input bg-card px-4 py-2.5 outline-none focus:ring-2 focus:ring-ring"
            data-testid="trending-pin-input"
          />
          <datalist id="trending-names">
            {names.map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
          <button type="submit" disabled={busy || !input.trim()} className={btn} data-testid="trending-pin-add">
            <Pin className="h-4 w-4" /> Pin
          </button>
        </form>
        {cfg.pinned.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Nobody pinned.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {cfg.pinned.map((n, i) => (
              <li key={n} className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5">
                <span className="font-medium">{n}</span>
                {!shownNames.has(lc(n)) && (
                  <span className="text-xs text-amber-500">Not in a running show, so not shown</span>
                )}
                <span className="ml-auto flex gap-1">
                  <button className={btn} disabled={busy || i === 0} onClick={() => move(i, -1)} aria-label="Move up">
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    className={btn}
                    disabled={busy || i === cfg.pinned.length - 1}
                    onClick={() => move(i, 1)}
                    aria-label="Move down"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  <button className={btn} disabled={busy} onClick={() => unpin(n)} aria-label={`Unpin ${n}`}>
                    <X className="h-4 w-4" />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-8" data-testid="trending-preview">
        <h2 className="font-serif text-xl font-semibold">What visitors see now</h2>
        <p className="text-sm text-muted-foreground">The first five are shown on the site. Use Hide to remove someone.</p>
        {preview.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Nobody to show.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {preview.map((e, i) => {
              const pinned = cfg.pinned.some((n) => lc(n) === lc(e.actor_name));
              return (
                <li
                  key={`${e.actor_name}-${e.show_id}-${i}`}
                  className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 ${
                    i < 5 ? "border-border bg-card" : "border-dashed border-border/60 opacity-70"
                  }`}
                >
                  <span className="w-5 text-sm text-muted-foreground">{i + 1}</span>
                  <span className="min-w-0">
                    <span className="font-medium">{e.actor_name}</span>
                    <span className="block truncate text-sm text-muted-foreground">
                      {e.role} &middot; {e.show_name}
                    </span>
                  </span>
                  {pinned && <span className="ml-auto text-xs text-[#C89B3C]">Pinned</span>}
                  {!pinned && (
                    <button
                      className={`${btn} ml-auto`}
                      disabled={busy}
                      onClick={() => hide(e.actor_name)}
                      data-testid={`trending-hide-${i}`}
                    >
                      <EyeOff className="h-4 w-4" /> Hide
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="mt-8" data-testid="trending-hidden">
        <h2 className="font-serif text-xl font-semibold">Hidden</h2>
        {cfg.hidden.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Nobody hidden.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {cfg.hidden.map((n) => (
              <li key={n} className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5">
                <span className="font-medium">{n}</span>
                <button className={`${btn} ml-auto`} disabled={busy} onClick={() => unhide(n)}>
                  <Eye className="h-4 w-4" /> Unhide
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}